import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card, Field, Area, SaveButton } from "../ui";

const SELECT = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";

const EMPTY = { title: "", slug: "", excerpt: "", cover_image: "", author: "Hola Movers", tags: "", body: "", meta_title: "", meta_description: "", status: "draft" };

function toForm(p) {
  return { ...p, tags: (p.tags || []).join(", ") };
}
function toPayload(f) {
  return { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
}

export default function BlogManager() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // form object or null
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/blog/posts").then((res) => setRows(res.data));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (p) => setEditing(toForm(p));

  const save = async () => {
    if (!editing.title) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = toPayload(editing);
    try {
      if (editing.id) {
        await api.put(`/admin/blog/posts/${editing.id}`, payload);
      } else {
        await api.post("/admin/blog/posts", payload);
      }
      toast.success("Post saved");
      setEditing(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/blog/posts/${id}`);
    toast.success("Post deleted");
    load();
  };

  const set = (k, v) => setEditing({ ...editing, [k]: v });

  if (editing) {
    return (
      <div data-testid="admin-blog-editor">
        <div className="flex items-center justify-between mb-6">
          <SectionTitle title={editing.id ? "Edit post" : "New post"} />
          <button data-testid="blog-editor-close" onClick={() => setEditing(null)} className="text-charcoal-500 hover:text-terracotta inline-flex items-center gap-1 text-sm"><X size={16} /> Cancel</button>
        </div>
        <div className="space-y-6">
          <Card>
            <div className="space-y-4">
              <Field label="Title" testid="blog-title" value={editing.title} onChange={(v) => set("title", v)} />
              <Field label="Slug (optional — auto-generated from title)" testid="blog-slug" value={editing.slug} onChange={(v) => set("slug", v)} placeholder="my-post-url" />
              <Field label="Cover image URL" testid="blog-cover" value={editing.cover_image} onChange={(v) => set("cover_image", v)} />
              <Field label="Author" testid="blog-author" value={editing.author} onChange={(v) => set("author", v)} />
              <Field label="Tags (comma-separated)" testid="blog-tags" value={editing.tags} onChange={(v) => set("tags", v)} placeholder="Mérida, Getting started" />
              <Area label="Excerpt (short summary shown on cards)" testid="blog-excerpt" value={editing.excerpt} onChange={(v) => set("excerpt", v)} rows={2} />
              <Area label="Body (Markdown supported: ## headings, **bold**, - lists, [links](/book))" testid="blog-body" value={editing.body} onChange={(v) => set("body", v)} rows={14} />
            </div>
          </Card>
          <Card>
            <h3 className="font-medium text-charcoal-800 mb-4">SEO & publishing</h3>
            <div className="space-y-4">
              <Field label="Meta title (optional)" testid="blog-meta-title" value={editing.meta_title} onChange={(v) => set("meta_title", v)} />
              <Area label="Meta description (optional)" testid="blog-meta-desc" value={editing.meta_description} onChange={(v) => set("meta_description", v)} rows={2} />
              <div>
                <label className="text-sm font-medium text-charcoal-600 mb-1.5 block">Status</label>
                <select data-testid="blog-status" className={SELECT} value={editing.status} onChange={(e) => set("status", e.target.value)}>
                  <option value="draft">Draft (hidden)</option>
                  <option value="published">Published (live)</option>
                </select>
              </div>
            </div>
          </Card>
          <SaveButton testid="blog-save" onClick={save} saving={saving} children={editing.id ? "Save post" : "Create post"} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-6">
        <SectionTitle title="Blog" subtitle="Write and manage articles. Drafts are hidden until published." />
        <button data-testid="blog-new" onClick={startNew} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">
          <Plus size={16} /> New post
        </button>
      </div>

      <div className="space-y-4">
        {rows.length === 0 && <p className="text-charcoal-500">No posts yet.</p>}
        {rows.map((p) => (
          <Card key={p.id}>
            <div className="flex gap-4 items-center">
              {p.cover_image && <img src={p.cover_image} alt={p.title} className="h-16 w-24 shrink-0 rounded-xl object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-charcoal-800 truncate">{p.title}</h3>
                  <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${p.status === "published" ? "bg-sage-light text-sage-700" : "bg-sand-200 text-charcoal-500"}`}>
                    {p.status}
                  </span>
                </div>
                <p className="text-sm text-charcoal-500 truncate">/blog/{p.slug}</p>
              </div>
              <button data-testid={`blog-edit-${p.id}`} onClick={() => startEdit(p)} className="text-charcoal-400 hover:text-terracotta px-2"><Pencil size={16} /></button>
              <button data-testid={`blog-delete-${p.id}`} onClick={() => remove(p.id)} className="text-charcoal-400 hover:text-destructive px-2"><Trash2 size={16} /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
